"use client";

import { UserPlus } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui/button";
import { useLoginRequired } from "@/hooks/auth/useLoginRequired";
import { useFetchCandidates } from "@/hooks/dao/election/useFetchCandidates";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useVotingPeriod } from "@/hooks/dao/useVotingPeriod";
import { useRouter } from "@/i18n/navigation";

/**
 * Variants per Figma 5462:51594 (disabled, pre-election) + 5462:51697 (active, yellow).
 *
 * - disabled: bg stroke-light/40, border stroke-light, text-text-disabled (Button colors="accent" disabled compound)
 * - active: bg-accent (#ffeb00), text-accent-foreground, cursor-pointer (Button primitive base)
 *
 * Click navigates to /info/dao/candidate (page-form, AC-1.9 / redesign).
 * Disabled gates (AC-1.7 already-candidate, AC-1.8 outside voting period) preserved.
 * Login-required wrap preserved (AC-1.9: wallet connect → then navigate).
 */
export const BeCandidateButton: React.FC = () => {
  const t = useTranslations("dao");
  const router = useRouter();
  const { address } = useAccount();
  const { cycle } = useCurrentCycle();
  const { isVotingPeriod } = useVotingPeriod(cycle);
  const { data: candidates } = useFetchCandidates(cycle);
  const handleLoginRequired = useLoginRequired();

  const isAlreadyCandidate = candidates?.some(
    (c) => c.walletAddress === address?.toLowerCase(),
  );

  const disabled = !isVotingPeriod || isAlreadyCandidate;

  const handleClick = handleLoginRequired(() => {
    if (!disabled) {
      router.push("/info/dao/candidate");
    }
  });

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={t("election.enroll.aria")}
      variant="solid"
      colors="accent"
      size="md"
    >
      <span className="text-sm font-semibold leading-[1.4]">
        {t("election.enroll.label")}
      </span>
      <UserPlus className="size-5" aria-hidden="true" />
    </Button>
  );
};
